import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown } = FiIcons;

const Home = () => {
  const line1 = "TRUST MC";
  const line2 = "FRANKEN";

  const containerVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.5
      }
    }
  };

  const letterVariants = {
    initial: { y: 100, opacity: 0, rotateX: -90 },
    animate: { 
      y: 0, 
      opacity: 1, 
      rotateX: 0,
      transition: { 
        duration: 0.8, 
        ease: [0.33, 1, 0.68, 1] 
      }
    },
    hover: {
      y: -10,
      scale: 1.1,
      rotateY: 360,
      color: "#ffffff",
      textShadow: "0 0 20px rgba(255,255,255,0.8)",
      transition: { 
        duration: 0.6,
        type: "spring",
        stiffness: 260,
        damping: 20
      }
    }
  };

  return (
    <div className="bg-black">
      {/* Hero Header with Video */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <video 
          autoPlay 
          loop 
          muted 
          playsInline 
          className="absolute inset-0 w-full h-full object-cover opacity-60 scale-105"
        >
          <source src="https://grafiken.trust-franken.de/HP/V1.mov" type="video/mp4" />
        </video>
        
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
        
        <div className="relative z-10 text-center px-6">
          <motion.img 
            src="https://grafiken.trust-franken.de/HP/Kreuz.png" 
            alt="Logo" 
            className="h-40 md:h-64 mx-auto mb-8 cursor-pointer"
            initial={{ opacity: 0, scale: 0.5, rotate: -180 }}
            animate={{ opacity: 1, scale: 1, rotate: 0, y: [0, -20, 0] }}
            whileHover={{ scale: 1.1, filter: 'drop-shadow(0 0 30px rgba(255,255,255,0.5))', rotate: 5 }}
            transition={{ duration: 1.5, y: { duration: 4, repeat: Infinity, ease: "easeInOut" } }}
          />
          
          <div className="perspective-1000">
            <motion.h1 
              variants={containerVariants}
              initial="initial"
              animate="animate"
              className="text-6xl md:text-9xl font-black tracking-tighter text-white mb-4 cursor-default flex flex-col items-center"
            >
              <div className="flex overflow-hidden py-2">
                {line1.split("").map((char, i) => (
                  <motion.span
                    key={i}
                    variants={letterVariants}
                    whileHover="hover"
                    className="inline-block"
                  >
                    {char === " " ? "\u00A0" : char}
                  </motion.span>
                ))}
              </div>
              <div className="flex overflow-hidden py-2">
                {line2.split("").map((char, i) => (
                  <motion.span
                    key={i}
                    variants={letterVariants}
                    whileHover="hover"
                    className="inline-block"
                  >
                    {char === " " ? "\u00A0" : char}
                  </motion.span>
                ))}
              </div>
            </motion.h1>
          </div>
          
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: "200px" }}
            transition={{ delay: 1.5, duration: 1 }}
            className="h-1 bg-gradient-to-r from-transparent via-white to-transparent mx-auto mb-6"
          />
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8, duration: 1 }}
            className="text-xl md:text-3xl text-gray-400 tracking-[0.4em] uppercase font-extralight"
          >
            Tradition • Respekt • Gemeinschaft
          </motion.p>
        </div>

        {/* Scroll Indicator Arrow */}
        <motion.div 
          animate={{ y: [0, 15, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 cursor-pointer opacity-50 hover:opacity-100 transition-opacity"
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
        >
          <SafeIcon icon={FiChevronDown} className="text-5xl text-white" />
        </motion.div>
      </section>

      {/* Intro Section */}
      <section className="py-32 px-6 relative overflow-hidden">
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 0.05, scale: 1.2 }}
          transition={{ duration: 2 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        >
          <img src="https://grafiken.trust-franken.de/HP/Kreuz.png" className="w-[800px] h-auto" alt="Background Logo" />
        </motion.div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-black uppercase mb-12"
          >
            Willkommen im Club
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl md:text-3xl text-gray-400 leading-relaxed font-light"
          >
            Seit unserer Gründung stehen wir für die unerschütterlichen Werte der Biker-Kultur. Der <span className="text-white font-bold">Trust MC Franken</span> ist mehr als nur ein Motorradclub – es ist eine Bruderschaft, die auf gegenseitigem Vertrauen und der Leidenschaft für die Straße basiert.
          </motion.p>
        </div>
      </section>
    </div>
  );
};

export default Home;